import { useEffect, useState } from "react";
import { Euro, TrendingUp, Users, ArrowUpRight, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";

/**
 * Admin revenue dashboard — Stripe is the source of truth, this page is the operator's
 * single-glance overview. Every figure pulls from the `payment_transactions` collection
 * (we mirror Stripe paid transactions there for fast querying).
 *
 * The "Withdraw" CTA opens the Stripe Dashboard (which handles automatic payouts to the
 * operator's bank account). We deliberately don't build a custom wallet — Stripe
 * handles all the regulated money-movement bits.
 */
export default function Revenue() {
    const [data, setData] = useState(null);
    const [error, setError] = useState("");

    useEffect(() => {
        let active = true;
        api.get("/admin/revenue").then((res) => {
            if (active) setData(res.data);
        }).catch((e) => {
            if (active) setError(e?.response?.data?.detail || "Could not load revenue");
        });
        return () => { active = false; };
    }, []);

    if (error) return <div className="border border-red-500/40 bg-red-500/[0.05] p-4 text-red-200" data-testid="revenue-error">{error}</div>;
    if (!data) return <div className="text-zinc-500 font-mono text-sm">Loading revenue…</div>;

    return (
        <div className="space-y-8" data-testid="revenue-page">
            <div className="flex items-end justify-between flex-wrap gap-4">
                <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500 mb-2">
                        // Revenue
                    </div>
                    <h1 className="font-heading font-black tracking-tighter text-4xl text-zinc-50">
                        Stripe revenue
                    </h1>
                    <p className="text-sm text-zinc-400 mt-2">
                        Live numbers from your payment_transactions ledger. Stripe handles automatic payouts to your bank.
                    </p>
                </div>
                <a
                    href={data.stripe_dashboard_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-black font-bold text-xs uppercase tracking-wider px-5 py-3"
                    data-testid="open-stripe-dashboard-btn"
                >
                    Open Stripe dashboard <ExternalLink className="w-3.5 h-3.5" />
                </a>
            </div>

            {/* Top KPI strip */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <KpiCard label="Today" data={data.today} icon={<TrendingUp className="w-3.5 h-3.5" />} />
                <KpiCard label="Last 7 days" data={data.last_7d} />
                <KpiCard label="Last 30 days" data={data.last_30d} accent />
                <KpiCard label="YTD" data={data.ytd} />
                <KpiCard label="Lifetime" data={data.lifetime} />
            </div>

            {/* Pro user count */}
            <div className="border border-zinc-800 bg-zinc-950 p-5 flex items-center gap-4" data-testid="pro-users-card">
                <div className="w-10 h-10 border border-amber-500/40 bg-amber-500/10 flex items-center justify-center">
                    <Users className="w-5 h-5 text-amber-400" />
                </div>
                <div>
                    <div className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">Active Pro subscribers</div>
                    <div className="font-heading font-black text-3xl text-zinc-50">{data.pro_users_active}</div>
                </div>
                <div className="ml-auto text-right">
                    <div className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">MRR (est.)</div>
                    <div className="font-heading font-bold text-xl text-emerald-400 font-mono">
                        €{(data.pro_users_active * 120).toLocaleString()}
                    </div>
                </div>
            </div>

            {/* Top customers */}
            <div data-testid="top-customers-section">
                <h2 className="font-heading font-bold text-lg text-zinc-50 mb-3">Top customers</h2>
                {data.top_customers.length === 0 ? (
                    <div className="border border-dashed border-zinc-800 bg-zinc-950/50 p-8 text-center font-mono text-xs text-zinc-600">
                        No paid customers yet. Your first €120 is going to feel great.
                    </div>
                ) : (
                    <div className="border border-zinc-800 bg-zinc-950 divide-y divide-zinc-900">
                        {data.top_customers.map((c, i) => (
                            <div key={c.email} className="flex items-center px-4 py-3 gap-3">
                                <span className="font-mono text-[10px] text-zinc-600 w-6">#{i + 1}</span>
                                <span className="text-sm text-zinc-200 flex-1 truncate">{c.email}</span>
                                <span className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">
                                    {c.count} purchase{c.count === 1 ? "" : "s"}
                                </span>
                                <span className="font-mono font-bold text-emerald-400 w-24 text-right">
                                    €{c.total.toFixed(2)}
                                </span>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Recent transactions */}
            <div data-testid="recent-tx-section">
                <h2 className="font-heading font-bold text-lg text-zinc-50 mb-3">Recent transactions</h2>
                {data.recent_transactions.length === 0 ? (
                    <div className="border border-dashed border-zinc-800 bg-zinc-950/50 p-8 text-center font-mono text-xs text-zinc-600">
                        No paid transactions yet.
                    </div>
                ) : (
                    <div className="border border-zinc-800 bg-zinc-950">
                        <div className="grid grid-cols-12 gap-2 px-4 py-2 border-b border-zinc-900 font-mono text-[9px] uppercase tracking-wider text-zinc-600">
                            <div className="col-span-4">Customer</div>
                            <div className="col-span-3">Product</div>
                            <div className="col-span-3">When</div>
                            <div className="col-span-2 text-right">Amount</div>
                        </div>
                        {data.recent_transactions.map((tx) => (
                            <div key={tx.completed_at + tx.user_email} className="grid grid-cols-12 gap-2 px-4 py-2.5 border-b border-zinc-900 last:border-0 text-sm items-center">
                                <div className="col-span-4 text-zinc-300 truncate">{tx.user_email}</div>
                                <div className="col-span-3 font-mono text-[10px] uppercase tracking-wider text-zinc-400">{tx.package_id}</div>
                                <div className="col-span-3 font-mono text-[10px] text-zinc-500">{new Date(tx.completed_at).toLocaleString()}</div>
                                <div className="col-span-2 text-right font-mono font-bold text-emerald-400">€{tx.amount.toFixed(2)}</div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

function KpiCard({ label, data, icon, accent }) {
    const eur = data?.eur || { total: 0, count: 0 };
    return (
        <div className={`border ${accent ? "border-amber-500/40" : "border-zinc-800"} bg-zinc-950 p-4`}>
            <div className="flex items-center justify-between font-mono text-[9px] uppercase tracking-wider text-zinc-500">
                <span>{label}</span>
                {icon}
            </div>
            <div className="font-heading font-black text-2xl text-zinc-50 mt-2 leading-none">
                €{eur.total.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
            </div>
            <div className="font-mono text-[10px] text-zinc-500 mt-1">
                {eur.count} txn{eur.count === 1 ? "" : "s"}
            </div>
        </div>
    );
}
